import React, { useEffect, useRef, useState } from "react";
import { api, resolvePhotoUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = [
    { code: "INR", symbol: "₹" }, { code: "USD", symbol: "$" }, { code: "EUR", symbol: "€" },
    { code: "GBP", symbol: "£" }, { code: "AED", symbol: "د.إ" }, { code: "JPY", symbol: "¥" },
];

export default function AdminSettings() {
    const { setSettings, refresh } = useSettings();
    const [form, setForm] = useState({
        store_name: "",
        currency_code: "INR",
        currency_symbol: "₹",
        logo_url: "",
        whats_app_number: "",
        whats_app_share_message: "",
    });
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        api.get("/settings").then((r) => setForm((f) => ({ ...f, ...r.data })));
    }, []);

    const uploadLogo = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, logo_url: data.url }));
            toast.success("Logo uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const clearLogo = () => setForm((f) => ({ ...f, logo_url: "" }));

    const save = async () => {
        try {
            const { data } = await api.put("/settings", form);
            setSettings(data);
            await refresh();
            toast.success("Settings saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    return (
        <section data-testid="admin-settings-page" className="max-w-xl">
            <h2 className="font-serif text-3xl mb-6">Storefront settings</h2>
            <div className="bg-card border border-border/60 rounded-2xl p-5 space-y-5">
                {/* Logo uploader */}
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em]">Store logo</Label>
                    <div className="mt-2 flex items-center gap-4">
                        <div
                            data-testid="settings-logo-preview"
                            className="h-20 w-20 rounded-2xl bg-surface/70 border border-border/60 overflow-hidden flex items-center justify-center shrink-0"
                        >
                            {form.logo_url ? (
                                <img
                                    src={resolvePhotoUrl(form.logo_url)}
                                    alt="Logo"
                                    className="w-full h-full object-contain"
                                />
                            ) : (
                                <ImageIcon className="h-6 w-6 text-muted-foreground" />
                            )}
                        </div>
                        <div className="flex-1 space-y-2">
                            <div className="flex flex-wrap gap-2">
                                <label className="cursor-pointer inline-flex items-center gap-2 text-sm rounded-full border border-border/60 px-4 py-2 hover:border-foreground">
                                    <Upload className="h-4 w-4" />
                                    {uploading ? "Uploading…" : (form.logo_url ? "Replace logo" : "Upload logo")}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        data-testid="settings-logo-input"
                                        onChange={(e) => uploadLogo(e.target.files?.[0])}
                                    />
                                </label>
                                {form.logo_url && (
                                    <button
                                        type="button"
                                        data-testid="settings-logo-clear"
                                        onClick={clearLogo}
                                        className="inline-flex items-center gap-1 text-sm rounded-full border border-border/60 px-4 py-2 hover:border-destructive hover:text-destructive"
                                    >
                                        <X className="h-4 w-4" />
                                        Remove
                                    </button>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                PNG / JPG / SVG. Square or wide logos work best; shown at ~32px in the header.
                            </p>
                        </div>
                    </div>
                </div>

                <div>
                    <Label className="text-xs uppercase tracking-[0.2em]">Store name</Label>
                    <Input
                        data-testid="settings-store-name"
                        value={form.store_name}
                        onChange={(e) => setForm({ ...form, store_name: e.target.value })}
                        className="mt-1 rounded-xl"
                    />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em]">Currency</Label>
                    <Select
                        value={form.currency_code}
                        onValueChange={(v) => {
                            const c = CURRENCIES.find((x) => x.code === v);
                            setForm({ ...form, currency_code: v, currency_symbol: c?.symbol || form.currency_symbol });
                        }}
                    >
                        <SelectTrigger data-testid="settings-currency-select" className="mt-1 rounded-xl">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {CURRENCIES.map((c) => (
                                <SelectItem key={c.code} value={c.code}>{c.symbol} · {c.code}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em]">Custom symbol (optional)</Label>
                    <Input
                        data-testid="settings-symbol-input"
                        value={form.currency_symbol}
                        onChange={(e) => setForm({ ...form, currency_symbol: e.target.value })}
                        className="mt-1 rounded-xl"
                    />
                </div>
                <hr className="border-border/40" />
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em]">WhatsApp number</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Include country code, no + or spaces (e.g. 919876543210)</p>
                    <Input
                        value={form.whats_app_number}
                        onChange={(e) => setForm({ ...form, whats_app_number: e.target.value })}
                        placeholder="919876543210"
                        className="mt-1 rounded-xl"
                    />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-[0.2em]">WhatsApp share message</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">The order link will be appended after this message.</p>
                    <Textarea
                        rows={3}
                        value={form.whats_app_share_message}
                        onChange={(e) => setForm({ ...form, whats_app_share_message: e.target.value })}
                        placeholder="Hi! Here are my order details:"
                        className="mt-1 rounded-xl"
                    />
                </div>
                <Button
                    data-testid="settings-save-btn"
                    onClick={save}
                    className="rounded-full bg-primary hover:bg-primary-hover"
                >
                    Save settings
                </Button>
            </div>
        </section>
    );
}
